import React, { useEffect, useState } from 'react';
import {
  ClassicEditor,
  AutoImage,
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  SimpleUploadAdapter,
  TextTransformation,
} from 'ckeditor5';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { userApi } from 'src/api/userApi';
import FileUploadDragDrop from 'src/components/widgets/fileUpload/FileUploadDragDrop';
import { Box, Button, Tabs, Tab, Skeleton } from '@mui/material';
import toast from 'react-hot-toast';
import CommentCardList from './CommentCardList';
import 'ckeditor5/ckeditor5.css';
import OmsLogs from './OmsLogs';
import { nanoid } from '@reduxjs/toolkit';
import useAuth from 'src/hooks/useAuth';

const OmsCommentsLogs = ({ details, commentApiToCall, logApiToCall }: any) => {
  const [commentValue, setCommentValue] = useState<string>('');
  const [filePath, setFilePath] = useState<string[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0); // State to manage the selected tab
  const { user } = useAuth();
  const fetchLogs = async () => {
    try {
      const response = await logApiToCall(details?._id);

      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const isNotEmpty = (val: string | any[]) => {
    if (Array.isArray(val)) {
      return val.length > 0;
    }
    return typeof val === 'string' && val.trim() !== '';
  };

  function uploadAdapter(loader) {
    return {
      upload: () =>
        new Promise((resolve) => {
          loader.file.then(async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            const image = await userApi.uploadAttachments(formData);
            resolve({
              default: `${image?.data?.blob_url}`,
            });
          });
        }),
    };
  }

  function uploadPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
      uploadAdapter(loader);
  }

  const editorConfig = {
    toolbar: {
      items: [
        '|',
        'heading',
        '|',
        'bold',
        'italic',
        '|',
        'link',
        'blockQuote',
        'insertImage',
        '|',
        'bulletedList',
        'numberedList',
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AutoImage,
      BlockQuote,
      Bold,
      Essentials,
      Heading,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      List,
      ListProperties,
      Paragraph,
      SimpleUploadAdapter,
      TextTransformation,
    ],
    blockToolbar: [
      'bold',
      'italic',
      '|',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      'outdent',
      'indent',
    ],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
      ],
    },
    image: {
      toolbar: [
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage',
      ],
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file',
          },
        },
      },
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },

    extraPlugins: [uploadPlugin],
    placeholder: 'Enter your comment here',
  };

  const [commentsArr, setCommentArr] = useState([]);
  const fileUploadRef = React.useRef(null);

  useEffect(() => {
    if (details?.comments) {
      setCommentArr(details?.comments);
    }
  }, [details]);
  const handleSubmit = async () => {
    try {
      if (isNotEmpty(commentValue) || isNotEmpty(filePath)) {
        const payload = {
          comment: {
            description: commentValue,
            attachments: filePath,
          },
          _id: details?._id,
        };
        const response = await commentApiToCall(payload);
        if (response?.status === 200) {
          const timestamp = new Date().toISOString();
          toast.success('Comment Added');
          setCommentArr((prev) => [
            ...prev,
            // creating dummy response object on FE because BE is not sending it back on comment creation even after requsting (ali)
            {
              description: commentValue,
              createdBy: {
                _id: user?.id,
                name: user?.name,
                email: user?.email,
                avatar: user?.image,
              },
              _id: nanoid(),
              updatedBy: {
                _id: user?.id,
                name: user?.name,
                email: user?.email,
                avatar: user?.image,
              },
              createdAt: timestamp,
              updatedAt: timestamp,
            },
          ]);
          setCommentValue('');
          setFilePath([]);
          fileUploadRef.current?.clearFiles();
        }
      }
    } catch (error) {
      // Handle error (e.g., show error message)
      console.error('Error submitting comment:', error);
    }
  };

  const handleCancel = () => {
    // Reset the comment and attachment state
    setCommentValue('');
    setFilePath([]);
    // Clear files in the FileUploadDragDrop component
    fileUploadRef.current?.clearFiles();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue); // Update the selected tab
  };

  useEffect(() => {
    if (details?._id) {
      fetchLogs();
    }
  }, [details?._id]);

  return (
    <div>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        textColor="secondary"
        indicatorColor="secondary"
        aria-label="comment and logs tabs">
        <Tab label="Comments" />
        <Tab label="Logs" />
      </Tabs>
      {tabValue === 0 && ( // Render comment component when Comments tab is selected
        <Box p={2}>
          <CKEditor
            editor={ClassicEditor}
            data={commentValue}
            onChange={(event, editor) => {
              const data = editor.getData();
              setCommentValue(data);
            }}
            config={editorConfig as any}
          />

          <FileUploadDragDrop
            ref={fileUploadRef}
            setFilePath={setFilePath}
            filePath={filePath}
          />
          <div>
            <Button onClick={handleCancel} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="secondary"
              sx={{ ml: 2 }}>
              Submit
            </Button>
          </div>
          <Box sx={{ backgroundColor: '#fff', mt: 1, borderRadius: 2 }}>
            <CommentCardList comments={commentsArr} />
          </Box>
        </Box>
      )}
      {tabValue === 1 && (
        <Box sx={{ backgroundColor: '#fff', mt: 1, borderRadius: 2 }}>
          <OmsLogs logsData={logs} />
        </Box>
      )}
    </div>
  );
};

export default OmsCommentsLogs;
